/**
 * WB4 Phase 3 — Palette intent proposal hook (rule-based NL, extends LLM augmentation).
 */

import { useMemo } from 'react';
import { omitUndefined } from '@shared/utils/omitUndefined';
import type { WorkbenchCommandContext } from '@shared/business-workbench/command-resolver';
import {
  buildPaletteIntentProposal,
  type PaletteIntentProposal,
} from '@shared/business-workbench/workbench-palette-intent-proposal';
import { resolvePaletteIntentFromNaturalLanguage } from '@shared/business-workbench/workbench-palette-intent';

export interface UsePaletteIntentProposalOptions {
  query: string;
  ctx: WorkbenchCommandContext;
  enabled?: boolean;
}

export interface PaletteIntentProposalResult {
  proposal?: PaletteIntentProposal;
}

export function usePaletteIntentProposal(
  options: UsePaletteIntentProposalOptions
): PaletteIntentProposalResult {
  const { query, ctx, enabled = true } = options;
  const trimmedQuery = query.trim();

  const proposal = useMemo(() => {
    if (!enabled || trimmedQuery.length < 2) {
      return undefined;
    }

    const objectId = ctx.object?.id;
    const resolution = resolvePaletteIntentFromNaturalLanguage({
      query: trimmedQuery,
      ...(ctx.businessDomain !== undefined ? { businessDomain: ctx.businessDomain } : {}),
      ...(objectId !== undefined ? { objectId } : {}),
    });

    if (!resolution) {
      return undefined;
    }

    return buildPaletteIntentProposal(resolution, ctx);
  }, [ctx, enabled, trimmedQuery]);

  return omitUndefined({ proposal });
}
